import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Badge,
  Button,
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  InfoBox,
  Input,
  Label,
  LoadingState,
  Toast,
} from '@biznomi/ui-react'
import {
  getDepartments,
  createDepartment,
  updateDepartment,
  deleteDepartment,
  type Department,
} from '@/lib/api/admin'

/**
 * 부서 관리 — ADMIN 전용.
 *
 * 이 화면이 생기기 전에는 부서를 손댈 방법이 없었다. 직원을 등록할 때 부서명을 쳐서
 * 자동으로 만들어지는 것이 전부라, 이름을 잘못 치면 부서가 하나 더 생기고 고칠 데가 없었다.
 *
 * ★ 인원수가 두 종류다. 서버가 일부러 나눠서 준다.
 *
 *     _count.employees   재직자만      → 화면에 보여주는 "N명"
 *     totalCount         퇴사자 포함   → **삭제 가능 여부**
 *
 *   퇴사자가 출퇴근 기록을 물고 있어서, 부서가 사라지면 그 기록의 소속이 끊긴다.
 *   그래서 서버는 퇴사자까지 세서 막는다. 화면이 재직자 수로 판단하면
 *   **"0명" 으로 보이는데 눌렀을 때 실패한다.** 그건 화면이 아니라 사고다.
 *   → totalCount 로 미리 비활성하고 이유를 적는다.
 */

interface EditState {
  dept: Department | null // null = 새로 만들기
  name: string
}

export default function Departments() {
  const qc = useQueryClient()
  const [edit, setEdit] = useState<EditState | null>(null)
  const [removing, setRemoving] = useState<Department | null>(null)
  const [saveError, setSaveError] = useState('')
  const [toast, setToast] = useState('')

  const { data, isLoading, error } = useQuery({
    queryKey: ['departments'],
    queryFn: async () => {
      const res = await getDepartments()
      if (!res.success) throw new Error(res.error ?? '부서를 불러오지 못했습니다')
      return res.data ?? []
    },
  })

  const save = useMutation({
    mutationFn: async (e: EditState) =>
      e.dept ? updateDepartment(e.dept.id, e.name.trim()) : createDepartment(e.name.trim()),
    onSuccess: (res) => {
      if (!res.success) {
        // 다이얼로그를 닫지 않는다 — 이름 중복처럼 고칠 수 있는 문제라 입력값을 남겨둔다
        setSaveError(res.error ?? '저장에 실패했습니다')
        return
      }
      setEdit(null)
      setSaveError('')
      setToast('저장했습니다')
      qc.invalidateQueries({ queryKey: ['departments'] })
    },
  })

  const remove = useMutation({
    mutationFn: (id: string) => deleteDepartment(id),
    onSuccess: (res) => {
      setRemoving(null)
      setToast(res.success ? '삭제했습니다' : (res.error ?? '삭제하지 못했습니다'))
      // 직원 목록·근태 화면의 부서 필터도 같이 상하므로 함께 무효화한다
      qc.invalidateQueries({ queryKey: ['departments'] })
    },
  })

  if (isLoading) return <LoadingState />
  if (error) return <InfoBox tone="danger">{(error as Error).message}</InfoBox>

  const departments = data ?? []

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-bold">부서</h1>
        <Button size="sm" onClick={() => setEdit({ dept: null, name: '' })}>
          부서 추가
        </Button>
      </header>

      {departments.length === 0 ? (
        <div className="rounded-card bg-card">
          <EmptyState
            title="등록된 부서가 없습니다."
            hint="부서를 먼저 만들어 두면 직원을 등록할 때 고를 수 있습니다."
            icon="inbox"
          />
        </div>
      ) : (
        <ul className="space-y-2">
          {departments.map((d) => {
            // ⚠ _count.employees 가 아니라 totalCount 다. 위 주석 참조.
            const locked = d.totalCount > 0
            const retired = d.totalCount - d._count.employees

            return (
              <li key={d.id} className="rounded-card bg-card p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 space-y-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="font-semibold">{d.name}</span>
                      {d._count.employees === 0 && !locked && (
                        <Badge variant="default">인원 없음</Badge>
                      )}
                    </div>

                    <p className="text-sm text-slate-500">{d._count.employees}명</p>

                    {locked && (
                      <p className="text-xs text-slate-400">
                        {retired > 0 && d._count.employees === 0
                          ? `퇴사자 ${retired}명이 남아 있어 삭제할 수 없습니다`
                          : '소속 직원이 있어 삭제할 수 없습니다'}
                      </p>
                    )}
                  </div>

                  <div className="flex shrink-0 gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => setEdit({ dept: d, name: d.name })}
                    >
                      이름 수정
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={locked}
                      onClick={() => setRemoving(d)}
                    >
                      삭제
                    </Button>
                  </div>
                </div>
              </li>
            )
          })}
        </ul>
      )}

      <Dialog
        open={edit !== null}
        onOpenChange={(o) => {
          if (!o) {
            setEdit(null)
            setSaveError('')
          }
        }}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{edit?.dept ? '부서 이름 수정' : '부서 추가'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-1.5">
            <Label htmlFor="dept-name">이름</Label>
            <Input
              id="dept-name"
              autoFocus
              value={edit?.name ?? ''}
              onChange={(e) => setEdit((s) => (s ? { ...s, name: e.target.value } : s))}
              placeholder="예: 간호부"
            />
          </div>

          {saveError && (
            <p
              role="alert"
              className="mt-3 rounded-lg bg-[var(--color-danger-bg)] px-3 py-2 text-sm text-[var(--color-danger)]"
            >
              {saveError}
            </p>
          )}

          <DialogFooter>
            <Button
              variant="secondary"
              className="flex-1"
              onClick={() => {
                setEdit(null)
                setSaveError('')
              }}
            >
              취소
            </Button>
            <Button
              className="flex-1"
              disabled={!edit?.name.trim() || edit.name.trim() === edit.dept?.name}
              loading={save.isPending}
              onClick={() => {
                setSaveError('')
                if (edit) save.mutate(edit)
              }}
            >
              저장
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(o) => !o && setRemoving(null)}
        title="이 부서를 삭제할까요?"
        message={`${removing?.name ?? ''}\n\n지난 출퇴근 기록에 남은 부서 이름은 그대로 보입니다.`}
        confirmText="삭제"
        destructive
        loading={remove.isPending}
        onConfirm={() => removing && remove.mutate(removing.id)}
      />

      {toast && <Toast message={toast} variant="success" onClose={() => setToast('')} />}
    </div>
  )
}
